import { Component, computed, effect, inject, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LoanListComponent } from './loan-list/loan-list.component';
import { Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoanService } from '../../services/loan.service';
import { NavigationService } from '../../services/navigation.service';
import { filter, firstValueFrom, merge, of, shareReplay, Subject, switchMap } from 'rxjs';
import { BREAKPOINT_MEDIUM } from '../../shared/constants/breakpoints.constants';

/**
 * Container component that displays a split view with loan list and loan summary
 */
@Component({
  selector: 'app-loan',
  templateUrl: './loan.component.html',
  styleUrl: './loan.component.scss',
  imports: [
    CommonModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    LoanListComponent,
    RouterModule
  ]
})
export class LoanComponent implements OnInit {
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #router = inject(Router);
  readonly #loanService = inject(LoanService);
  readonly #navigationService = inject(NavigationService);
  private refreshLoan$ = new Subject<string>();

  readonly id = input<string>();
  readonly sidenavWidth = '360px';

  // Check if screen is expanded size (BREAKPOINT_MEDIUM: 840px and above)
  readonly isExpanded = toSignal(
    this.#breakpointObserver.observe(`(min-width: ${BREAKPOINT_MEDIUM}px)`)
  );

  // Show sidenav on expanded screen or mobile when toggled
  readonly sidenavOpened = computed(() => {
    const isExpandedScreen = this.isExpanded()?.matches;

    // Expanded screen: always show sidenav
    // Mobile: show sidenav only when toggled open
    return isExpandedScreen || this.#navigationService.mobileNavOpened();
  });

  readonly sidenavMode = computed(() => {
    // Use 'side' mode on expanded screens, 'over' mode on mobile
    return this.isExpanded()?.matches ? 'side' : 'over';
  });

  async ngOnInit() {
    // Auto-navigate to first loan when no loan is selected
    if (this.id()) {
      return;
    }

    try {
      const loans = await firstValueFrom(this.#loanService.getLoans());
      if (loans.length > 0) {
        this.#router.navigate(['/loan', loans[0].number]);
      }
    } catch (error) {
      console.error('Error loading loans:', error);
      // Don't navigate on error
    }
  }

  /**
   * Toggle mobile sidenav open/closed state
   */
  toggleMobileNav(): void {
    this.#navigationService.toggleMobileNav();
  }

  /**
   * Handle backdrop click - close mobile sidenav
   */
  onBackdropClick(): void {
    this.#navigationService.closeMobileNav();
  }
}
